/**
 * Nutesty Shopify Theme - Main JavaScript
 * Handles all interactive features and animations
 */

(function() {
  'use strict';

  // ========================================
  // Utility Functions
  // ========================================

  const $ = (selector) => document.querySelector(selector);
  const $$ = (selector) => document.querySelectorAll(selector);

  const debounce = (func, wait) => {
    let timeout;
    return function executedFunction(...args) {
      const later = () => {
        clearTimeout(timeout);
        func(...args);
      };
      clearTimeout(timeout);
      timeout = setTimeout(later, wait);
    };
  };

  // ========================================
  // Header Scroll Effect
  // ========================================

  function initHeaderScroll() {
    const header = $('.header');
    if (!header) return;

    let lastScroll = 0;

    window.addEventListener('scroll', debounce(() => {
      const currentScroll = window.pageYOffset;

      if (currentScroll > 100) {
        header.classList.add('header--scrolled');
      } else {
        header.classList.remove('header--scrolled');
      }

      lastScroll = currentScroll;
    }, 10));
  }

  // ========================================
  // Hero Carousel
  // ========================================

  function initHeroCarousel() {
    const carousel = $('.hero-carousel');
    if (!carousel) return;

    const track = carousel.querySelector('.hero-carousel__track');
    const slides = carousel.querySelectorAll('.hero-carousel__slide');
    const dotsContainer = carousel.querySelector('.hero-carousel__controls');
    const prevBtn = carousel.querySelector('.hero-carousel__arrow--prev');
    const nextBtn = carousel.querySelector('.hero-carousel__arrow--next');

    if (!track || slides.length === 0) return;

    let currentSlide = 0;
    const slideCount = slides.length;
    let autoplayInterval;

    function updateCarousel() {
      track.style.transform = `translateX(-${currentSlide * 100}%)`;
    }

    function goToSlide(index) {
      currentSlide = index;
      updateCarousel();
      resetAutoplay();
    }

    function nextSlide() {
      currentSlide = (currentSlide + 1) % slideCount;
      updateCarousel();
    }

    function prevSlide() {
      currentSlide = (currentSlide - 1 + slideCount) % slideCount;
      updateCarousel();
    }

    function startAutoplay() {
      const autoplaySpeed = carousel.dataset.autoplaySpeed || 5000;
      autoplayInterval = setInterval(nextSlide, parseInt(autoplaySpeed));
    }

    function stopAutoplay() {
      clearInterval(autoplayInterval);
    }

    function resetAutoplay() {
      stopAutoplay();
      startAutoplay();
    }

    // Event listeners
    if (nextBtn) nextBtn.addEventListener('click', () => {
      nextSlide();
      resetAutoplay();
    });

    if (prevBtn) prevBtn.addEventListener('click', () => {
      prevSlide();
      resetAutoplay();
    });

    // Touch/swipe support
    let touchStartX = 0;
    let touchEndX = 0;

    carousel.addEventListener('touchstart', (e) => {
      touchStartX = e.changedTouches[0].screenX;
    });

    carousel.addEventListener('touchend', (e) => {
      touchEndX = e.changedTouches[0].screenX;
      handleSwipe();
    });

    function handleSwipe() {
      if (touchEndX < touchStartX - 50) {
        nextSlide();
        resetAutoplay();
      }
      if (touchEndX > touchStartX + 50) {
        prevSlide();
        resetAutoplay();
      }
    }

    // Start autoplay
    startAutoplay();

    // Pause on hover
    carousel.addEventListener('mouseenter', stopAutoplay);
    carousel.addEventListener('mouseleave', startAutoplay);
  }

  // ========================================
  // Product Carousel
  // ========================================

  function initProductCarousels() {
    const carousels = $$('.product-carousel');

    carousels.forEach(carousel => {
      const track = carousel.querySelector('.product-carousel__track');
      const prevBtn = carousel.querySelector('.carousel-arrow--prev');
      const nextBtn = carousel.querySelector('.carousel-arrow--next');

      if (!track) return;

      const scrollAmount = 300;

      if (prevBtn) {
        prevBtn.addEventListener('click', () => {
          track.scrollBy({
            left: -scrollAmount,
            behavior: 'smooth'
          });
        });
      }

      if (nextBtn) {
        nextBtn.addEventListener('click', () => {
          track.scrollBy({
            left: scrollAmount,
            behavior: 'smooth'
          });
        });
      }

      // Update button states based on scroll position
      function updateButtonStates() {
        if (!prevBtn || !nextBtn) return;

        const isAtStart = track.scrollLeft <= 0;
        const isAtEnd = track.scrollLeft >= track.scrollWidth - track.clientWidth - 10;

        prevBtn.style.opacity = isAtStart ? '0.5' : '1';
        prevBtn.style.pointerEvents = isAtStart ? 'none' : 'auto';
        nextBtn.style.opacity = isAtEnd ? '0.5' : '1';
        nextBtn.style.pointerEvents = isAtEnd ? 'none' : 'auto';
      }

      track.addEventListener('scroll', debounce(updateButtonStates, 100));
      updateButtonStates();
    });
  }

  // ========================================
  // Scroll Animations
  // ========================================

  function initScrollAnimations() {
    const elements = $$('.scroll-animate');
    if (elements.length === 0) return;

    // Make products visible immediately if they're already in viewport
    elements.forEach(el => {
      const rect = el.getBoundingClientRect();
      const isInViewport = rect.top < window.innerHeight && rect.bottom > 0;
      if (isInViewport) {
        el.classList.add('is-visible');
      }
    });

    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-visible');
          observer.unobserve(entry.target);
        }
      });
    }, {
      threshold: 0.1,
      rootMargin: '0px 0px -50px 0px'
    });

    elements.forEach(el => {
      if (!el.classList.contains('is-visible')) {
        observer.observe(el);
      }
    });
  }

  // ========================================
  // Mobile Menu
  // ========================================

  function initMobileMenu() {
    // Use setTimeout to ensure DOM is fully ready
    setTimeout(() => {
      const menuToggle = document.querySelector('.mobile-menu-toggle');
      const mobileMenu = document.querySelector('.mobile-menu');
      const overlay = document.querySelector('.mobile-menu-overlay');
      const closeBtn = document.querySelector('.mobile-menu__close');

      if (!menuToggle) {
        console.warn('Mobile menu toggle button not found');
        return;
      }

      if (!mobileMenu) {
        console.warn('Mobile menu element not found');
        return;
      }

      function openMenu(e) {
        if (e) {
          e.preventDefault();
          e.stopPropagation();
        }
        if (mobileMenu) {
          mobileMenu.classList.add('is-open');
        }
        if (overlay) {
          overlay.classList.add('is-visible');
        }
        document.body.style.overflow = 'hidden';
      }

      function closeMenu(e) {
        if (e) {
          e.preventDefault();
          e.stopPropagation();
        }
        if (mobileMenu) {
          mobileMenu.classList.remove('is-open');
        }
        if (overlay) {
          overlay.classList.remove('is-visible');
        }
        document.body.style.overflow = '';
      }

      // Add event listeners
      menuToggle.addEventListener('click', openMenu, { passive: false });
      
      if (closeBtn) {
        closeBtn.addEventListener('click', closeMenu, { passive: false });
      }
      if (overlay) {
        overlay.addEventListener('click', closeMenu, { passive: false });
      }
      
      // Close menu on ESC key
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && mobileMenu && mobileMenu.classList.contains('is-open')) {
          closeMenu();
        }
      });
    }, 100);
  }

  // ========================================
  // Add to Cart
  // ========================================

  function initAddToCart() {
    const addToCartButtons = $$('[data-add-to-cart]');

    addToCartButtons.forEach(button => {
      button.addEventListener('click', async (e) => {
        e.preventDefault();

        const variantId = button.dataset.variantId;
        if (!variantId) return;

        button.disabled = true;
        button.textContent = 'Adding...';

        try {
          const response = await fetch('/cart/add.js', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              id: variantId,
              quantity: 1
            })
          });

          if (response.ok) {
            const data = await response.json();
            updateCartCount();
            showNotification('Product added to cart!', 'success');
          } else {
            throw new Error('Failed to add to cart');
          }
        } catch (error) {
          console.error('Error adding to cart:', error);
          showNotification('Failed to add product to cart', 'error');
        } finally {
          button.disabled = false;
          button.textContent = 'Add to Cart';
        }
      });
    });
  }

  // ========================================
  // Update Cart Count
  // ========================================

  async function updateCartCount() {
    try {
      const response = await fetch('/cart.js');
      const cart = await response.json();
      const cartCountElements = $$('.cart-count__badge');
      
      cartCountElements.forEach(element => {
        element.textContent = cart.item_count;
        if (cart.item_count > 0) {
          element.style.display = 'flex';
        }
      });
    } catch (error) {
      console.error('Error updating cart count:', error);
    }
  }

  // ========================================
  // Cart Drawer
  // ========================================

  // Cart Drawer - Disabled (removed to go directly to cart page)
  function initCartDrawer() {
    // Cart drawer functionality removed - cart icon now navigates directly to cart page
    return;
  }

  // ========================================
  // Search Functionality
  // ========================================

  function initSearch() {
    // Mobile In-Header Search
    const mobileSearchIcon = $('.mobile-search-icon');
    const mobileSearchBar = $('.header__mobile-search-bar');
    const mobileSearchClose = $('.header__mobile-search-close');
    const mobileSearchInput = $('.header__mobile-search-bar .header__search-input');
    const mobileSearchResults = $('.header__mobile-search-results');

    if (mobileSearchIcon && mobileSearchBar) {
      mobileSearchIcon.addEventListener('click', (e) => {
        e.preventDefault();
        mobileSearchBar.classList.add('is-active');
        mobileSearchInput?.focus();
      });

      if (mobileSearchClose) {
        mobileSearchClose.addEventListener('click', () => {
          mobileSearchBar.classList.remove('is-active');
          if (mobileSearchInput) mobileSearchInput.value = '';
          if (mobileSearchResults) mobileSearchResults.innerHTML = '';
        });
      }

      // Live Search for Mobile
      if (mobileSearchInput) {
        mobileSearchInput.addEventListener('input', debounce(async (e) => {
          const query = e.target.value.trim();
          if (query.length < 2) {
            if (mobileSearchResults) mobileSearchResults.innerHTML = '';
            return;
          }
          try {
            const response = await fetch(`/search/suggest.json?q=${encodeURIComponent(query)}&resources[type]=product`);
            const data = await response.json();
            renderSearchResults(data.resources.results.products, mobileSearchResults);
          } catch (error) {
            console.error('Search error:', error);
          }
        }, 300));
      }
    }

    // Desktop Search (Keep existing if needed, or unify)
    const searchToggle = $('.header__search-toggle'); // If desktop has a toggle
    // ... (Desktop logic can remain or be adapted)

    function renderSearchResults(products, container) {
      if (!container) return;

      if (!products || products.length === 0) {
        container.innerHTML = '<p class="search-modal__no-results">No products found</p>';
        return;
      }

      container.innerHTML = products.slice(0, 5).map(product => `
        <a href="${product.url}" class="search-result">
          <img src="${product.image}" alt="${product.title}" class="search-result__image">
          <div class="search-result__details">
            <h4 class="search-result__title">${product.title}</h4>
            <p class="search-result__price">$${product.price}</p>
          </div>
        </a>
      `).join('');
    }
  }

  // ========================================
  // Notification System
  // ========================================

  function showNotification(message, type = 'success') {
    const notification = document.createElement('div');
    notification.className = `notification notification--${type} is-visible`;
    notification.textContent = message;
    document.body.appendChild(notification);

    setTimeout(() => {
      notification.classList.remove('is-visible');
      setTimeout(() => notification.remove(), 300);
    }, 3000);
  }

  // ========================================
  // Newsletter Form
  // ========================================

  function initNewsletter() {
    const forms = $$('.newsletter-form');

    forms.forEach(form => {
      form.addEventListener('submit', async (e) => {
        e.preventDefault();

        const email = form.querySelector('input[type="email"]').value;
        const submitBtn = form.querySelector('button[type="submit"]');

        if (!email) return;

        submitBtn.disabled = true;
        submitBtn.textContent = 'Subscribing...';

        // Simulate newsletter signup (replace with actual API call)
        setTimeout(() => {
          showNotification('Successfully subscribed to newsletter!', 'success');
          form.reset();
          submitBtn.disabled = false;
          submitBtn.textContent = 'Subscribe';
        }, 1000);
      });
    });
  }

  // ========================================
  // Lazy Loading Images
  // ========================================

  function initLazyLoading() {
    const images = $$('img[loading="lazy"]');

    if ('loading' in HTMLImageElement.prototype) {
      // Browser supports native lazy loading
      return;
    }

    // Fallback for browsers that don't support lazy loading
    const imageObserver = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          const img = entry.target;
          img.src = img.dataset.src;
          img.classList.add('loaded');
          imageObserver.unobserve(img);
        }
      });
    });

    images.forEach(img => imageObserver.observe(img));
  }

  // ========================================
  // Initialize All Features
  // ========================================

  function init() {
    // Critical: Run immediately
    initHeaderScroll();
    initMobileMenu();
    initAddToCart();
    initCartDrawer();
    updateCartCount();
    
    // Non-critical: Defer heavy operations
    if (window.requestIdleCallback) {
      requestIdleCallback(() => {
        initHeroCarousel();
        initProductCarousels();
        initScrollAnimations();
        initSearch();
        initNewsletter();
        initLazyLoading();
      }, { timeout: 1000 });
    } else {
      setTimeout(() => {
        initHeroCarousel();
        initProductCarousels();
        initScrollAnimations();
        initSearch();
        initNewsletter();
        initLazyLoading();
      }, 100);
    }
  }

  // Run on DOM ready - Optimized for faster initialization
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    // DOM already loaded, run immediately but defer heavy operations
    if (window.requestIdleCallback) {
      requestIdleCallback(init, { timeout: 2000 });
    } else {
      setTimeout(init, 0);
    }
  }

})();

// Animated Search Placeholder
document.addEventListener('DOMContentLoaded', function() {
  const searchInput = document.querySelector('[data-animated-placeholder]');
  
  if (searchInput) {
    const placeholders = [
      'Search for "Spices"',
      'Search for "Ghee"',
      'Search for "Tea"',
      'Search for "Honey"',
      'Search for "Saffron"'
    ];
    
    let currentIndex = 0;
    let charIndex = 0;
    let isDeleting = false;
    let currentText = '';
    
    function animatePlaceholder() {
      const currentPlaceholder = placeholders[currentIndex];
      
      if (isDeleting) {
        currentText = currentPlaceholder.substring(0, charIndex - 1);
        charIndex--;
      } else {
        currentText = currentPlaceholder.substring(0, charIndex + 1);
        charIndex++;
      }
      
      searchInput.setAttribute('placeholder', currentText);
      
      let typeSpeed = isDeleting ? 50 : 100;
      
      if (!isDeleting && charIndex === currentPlaceholder.length) {
        typeSpeed = 2000; // Pause at end
        isDeleting = true;
      } else if (isDeleting && charIndex === 0) {
        isDeleting = false;
        currentIndex = (currentIndex + 1) % placeholders.length;
        typeSpeed = 500; // Pause before next word
      }
      
      setTimeout(animatePlaceholder, typeSpeed);
    }
    
    // Start animation
    setTimeout(animatePlaceholder, 0);
  }
});
